import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSpotlight } from '../contexts/SpotlightContext';
import { SpotlightOverlay } from './SpotlightOverlay';
import { SOSButton } from './SOSButton';
import { ThemeToggle } from './ThemeToggle';
import { FloatingInstallShare } from './FloatingInstallShare';
import { MobileBottomNav } from './MobileBottomNav';
import { OfflineBadge } from './OfflineBadge';
import {
  LayoutDashboard, Users, Calendar, MessageSquare, FileText,
  LogOut, Menu, X, Bell, Building2, UserPlus,
  ClipboardList, BarChart3, Shield, Briefcase, Heart, Home,
  ArrowLeftRight, CalendarOff, Star, Wallet, Megaphone, BookOpen,
  Pill, AlertTriangle, FolderOpen, Upload, Sparkles, Settings,
  ClipboardCheck, Trophy, DollarSign, ArrowRightLeft, PhoneCall, CreditCard,
  ScrollText, Receipt, Lock, ShieldCheck, Rocket, CalendarClock, Award, KeyRound,
  ChevronDown, Radio, ListChecks, Phone, Target, Camera, ThumbsUp, Image, Video, Clock, Scale,
  Activity, Fingerprint, Stethoscope, CheckCircle2, MapPin, Wrench
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

// Grouped menu structure for roles with many items
const roleMenuConfig = {
  agency_admin: {
    standalone: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/admin' },
    ],
    groups: [
      {
        label: 'People',
        icon: Users,
        items: [
          { icon: Users, label: 'Caregivers', path: '/dashboard/admin/caregivers' },
          { icon: Heart, label: 'Clients', path: '/dashboard/admin/clients' },
        ]
      },
      {
        label: 'Scheduling',
        icon: Calendar,
        items: [
          { icon: Radio, label: 'Live Tracking', path: '/live-tracking' },
          { icon: Calendar, label: 'Shifts', path: '/dashboard/admin/shifts' },
          { icon: ArrowLeftRight, label: 'Shift Swaps', path: '/shift-swaps' },
          { icon: Sparkles, label: 'AI Scheduling', path: '/ai-scheduling' },
          { icon: CalendarOff, label: 'Leave Requests', path: '/leave-requests' },
          { icon: Users, label: 'Staff Availability', path: '/availability' },
        ]
      },
      {
        label: 'Recruitment',
        icon: Briefcase,
        items: [
          { icon: Briefcase, label: 'Job Postings', path: '/manage-jobs' },
          { icon: PhoneCall, label: 'Callbacks', path: '/callback-requests' },
          { icon: CalendarClock, label: 'Interviews', path: '/interviews' },
        ]
      },
      {
        label: 'HR & Development',
        icon: ClipboardCheck,
        items: [
          { icon: ClipboardCheck, label: 'HR & Reviews', path: '/performance-reviews' },
          { icon: BookOpen, label: 'Learning', path: '/learning' },
          { icon: Award, label: 'Rewards', path: '/rewards-admin' },
          { icon: DollarSign, label: 'Payroll', path: '/payroll' },
          { icon: ClipboardCheck, label: 'Training Matrix', path: '/training-matrix' },
          { icon: Award, label: 'Caregiver Scorecard', path: '/caregiver-scorecard' },
          { icon: Shield, label: 'HR Compliance', path: '/hr-compliance' },
          { icon: BarChart3, label: 'Compliance Scores', path: '/compliance-scores' },
          { icon: ClipboardCheck, label: 'Compliance Checklists', path: '/compliance-checklists' },
        ]
      },
      {
        label: 'Communication',
        icon: MessageSquare,
        items: [
          { icon: MessageSquare, label: 'Messages', path: '/messaging' },
          { icon: Users, label: 'Team Chat', path: '/team-chat' },
          { icon: Megaphone, label: 'Broadcasts', path: '/broadcasts' },
          { icon: Bell, label: 'Notifications', path: '/admin/notifications' },
        ]
      },
      {
        label: 'Records',
        icon: FileText,
        items: [
          { icon: FileText, label: 'Timesheets', path: '/timesheets' },
          { icon: Receipt, label: 'Invoices', path: '/invoices' },
          { icon: BookOpen, label: 'Care Journals', path: '/care-journals' },
          { icon: AlertTriangle, label: 'Incidents', path: '/incident-reports' },
          { icon: FolderOpen, label: 'Client Folders', path: '/client-folders' },
          { icon: Heart, label: 'DSCR Records', path: '/dashboard/admin/clients' },
          { icon: Shield, label: 'Clinical Compliance', path: '/clinical-compliance' },
          { icon: Pill, label: 'eMAR', path: '/medication-round' },
          { icon: Activity, label: 'Health & Quality', path: '/health-quality' },
          { icon: Stethoscope, label: 'NHS Connect', path: '/nhs-connect' },
          { icon: MapPin, label: 'Visit Audit Trail', path: '/visit-audit' },
          { icon: MapPin, label: 'Visit Heatmap', path: '/visit-heatmap' },
        ]
      },
      {
        label: 'Documents',
        icon: FolderOpen,
        items: [
          { icon: FileText, label: 'Templates', path: '/document-templates' },
          { icon: Heart, label: 'Care Templates', path: '/care-plan-templates' },
          { icon: FolderOpen, label: 'Storage', path: '/document-storage' },
          { icon: Shield, label: 'Compliance', path: '/caregiver-compliance' },
        ]
      },
      {
        label: 'Organization',
        icon: Building2,
        items: [
          { icon: UserPlus, label: 'Invitations', path: '/dashboard/admin/invitations' },
          { icon: Users, label: 'Bulk Invite', path: '/bulk-invite' },
          { icon: ArrowRightLeft, label: 'Networks', path: '/networks' },
          { icon: Wallet, label: 'Bank Details', path: '/staff-bank-details' },
          { icon: Shield, label: 'Discipline', path: '/staff-discipline' },
          { icon: ShieldCheck, label: 'Verification', path: '/verification' },
          { icon: Rocket, label: 'Onboarding', path: '/onboarding' },
        ]
      },
      {
        label: 'Admin Tools',
        icon: Settings,
        items: [
          { icon: KeyRound, label: 'Permissions', path: '/permissions' },
          { icon: ScrollText, label: 'Audit Logs', path: '/audit-logs' },
          { icon: Lock, label: 'Security', path: '/security-settings' },
          { icon: CreditCard, label: 'Billing', path: '/subscription' },
          { icon: BarChart3, label: 'Analytics', path: '/analytics' },
          { icon: Scale, label: 'Governance', path: '/governance' },
          { icon: Fingerprint, label: 'IG & GDPR', path: '/ig-compliance' },
          { icon: Shield, label: 'DSPT Toolkit', path: '/dspt-dashboard' },
          { icon: CheckCircle2, label: 'CQC Readiness', path: '/cqc-readiness' },
          { icon: ClipboardCheck, label: 'DSPT Assessment', path: '/dspt-assessment' },
          { icon: Award, label: 'Compliance Certificate', path: '/compliance-certificate' },
          { icon: Shield, label: 'HIQA Assessment', path: '/hiqa-assessment' },
        ]
      },
    ]
  },
  caregiver: {
    standalone: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/caregiver' },
    ],
    groups: [
      {
        label: 'Shifts',
        icon: Calendar,
        items: [
          { icon: Calendar, label: 'My Shifts', path: '/dashboard/caregiver/shifts' },
          { icon: ClipboardList, label: 'Available Shifts', path: '/dashboard/caregiver/available' },
          { icon: Clock, label: 'My Availability', path: '/availability' },
          { icon: ArrowLeftRight, label: 'Shift Swaps', path: '/shift-swaps' },
          { icon: CalendarOff, label: 'Leave', path: '/leave-requests' },
        ]
      },
      {
        label: 'Daily Work',
        icon: ListChecks,
        items: [
          { icon: ListChecks, label: 'Tasks', path: '/my-tasks' },
          { icon: BookOpen, label: 'Care Journals', path: '/care-journals' },
          { icon: Pill, label: 'Medications', path: '/medications' },
          { icon: Pill, label: 'Med Round', path: '/medication-round' },
          { icon: AlertTriangle, label: 'Incidents', path: '/incident-reports' },
          { icon: Activity, label: 'Health Obs', path: '/health-quality' },
          { icon: MapPin, label: 'Visit History', path: '/visit-audit' },
        ]
      },
      {
        label: 'Communication',
        icon: MessageSquare,
        items: [
          { icon: MessageSquare, label: 'Messages', path: '/messaging' },
          { icon: Users, label: 'Team Chat', path: '/team-chat' },
          { icon: Bell, label: 'Notifications', path: '/my-notifications' },
        ]
      },
      {
        label: 'Career',
        icon: Trophy,
        items: [
          { icon: Trophy, label: 'Learning', path: '/learning' },
          { icon: Award, label: 'Certifications', path: '/my-certifications' },
          { icon: Star, label: 'Rewards', path: '/my-rewards' },
          { icon: ClipboardCheck, label: 'My Reviews', path: '/performance-reviews' },
        ]
      },
      {
        label: 'Records',
        icon: FileText,
        items: [
          { icon: Wallet, label: 'Earnings', path: '/my-profile' },
          { icon: DollarSign, label: 'My Payslips', path: '/payroll' },
          { icon: FileText, label: 'Timesheets', path: '/timesheets' },
          { icon: FolderOpen, label: 'Documents', path: '/document-storage' },
          { icon: Shield, label: 'My Compliance', path: '/my-compliance' },
        ]
      },
      {
        label: 'Safety',
        icon: Phone,
        items: [
          { icon: Phone, label: 'Emergency Info', path: '/emergency-info' },
          { icon: Target, label: 'Wellness Goals', path: '/wellness-goals' },
          { icon: ArrowRightLeft, label: 'Networks', path: '/networks' },
        ]
      },
    ]
  },
  ceo: {
    standalone: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/ceo' },
    ],
    groups: [
      {
        label: 'Management',
        icon: Shield,
        items: [
          { icon: Shield, label: 'Admin Management', path: '/dashboard/ceo/admins' },
          { icon: Users, label: 'All Staff', path: '/dashboard/ceo/staff' },
        ]
      },
      {
        label: 'Operations',
        icon: Calendar,
        items: [
          { icon: Calendar, label: 'Shifts', path: '/dashboard/admin/shifts' },
          { icon: ArrowLeftRight, label: 'Shift Swaps', path: '/shift-swaps' },
          { icon: Sparkles, label: 'AI Scheduling', path: '/ai-scheduling' },
          { icon: CalendarOff, label: 'Leave Requests', path: '/leave-requests' },
          { icon: Briefcase, label: 'Job Postings', path: '/manage-jobs' },
          { icon: PhoneCall, label: 'Callbacks', path: '/callback-requests' },
        ]
      },
      {
        label: 'Reporting',
        icon: BarChart3,
        items: [
          { icon: BarChart3, label: 'Analytics', path: '/analytics' },
          { icon: FileText, label: 'Timesheets', path: '/timesheets' },
          { icon: AlertTriangle, label: 'Incidents', path: '/incident-reports' },
          { icon: Megaphone, label: 'Broadcasts', path: '/broadcasts' },
          { icon: Activity, label: 'Health & Quality', path: '/health-quality' },
          { icon: Stethoscope, label: 'NHS Connect', path: '/nhs-connect' },
        ]
      },
      {
        label: 'Admin',
        icon: Settings,
        items: [
          { icon: CreditCard, label: 'Billing', path: '/subscription' },
          { icon: Lock, label: 'Security', path: '/security-settings' },
          { icon: Building2, label: 'Organization', path: '/dashboard/ceo/organization' },
          { icon: Fingerprint, label: 'IG & GDPR', path: '/ig-compliance' },
          { icon: Shield, label: 'DSPT Toolkit', path: '/dspt-dashboard' },
          { icon: CheckCircle2, label: 'CQC Readiness', path: '/cqc-readiness' },
          { icon: ClipboardCheck, label: 'DSPT Assessment', path: '/dspt-assessment' },
        ]
      },
    ]
  },
  client: {
    standalone: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/client' },
    ],
    groups: [
      {
        label: 'Care',
        icon: Heart,
        items: [
          { icon: Calendar, label: 'My Schedule', path: '/dashboard/client/schedule' },
          { icon: Radio, label: 'Care Tracking', path: '/client/care-tracking' },
          { icon: Users, label: 'My Caregivers', path: '/client/caregivers' },
          { icon: FileText, label: 'Care Plan', path: '/dashboard/client/care-plan' },
          { icon: BookOpen, label: 'Care Journals', path: '/care-journals' },
          { icon: Pill, label: 'Medications', path: '/medications' },
        ]
      },
      {
        label: 'My Account',
        icon: Settings,
        items: [
          { icon: Settings, label: 'Profile', path: '/client/profile' },
          { icon: Image, label: 'Photos', path: '/client/photos' },
          { icon: FolderOpen, label: 'Documents', path: '/client/documents' },
        ]
      },
      {
        label: 'Feedback',
        icon: Star,
        items: [
          { icon: Star, label: 'Rate Agency', path: '/client/rate-agency' },
          { icon: ThumbsUp, label: 'Rate Caregiver', path: '/client/rate-caregiver' },
          { icon: Megaphone, label: 'Broadcasts', path: '/broadcasts' },
        ]
      },
    ]
  },
  family: {
    standalone: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/family' },
    ],
    groups: [
      {
        label: 'Care',
        icon: Heart,
        items: [
          { icon: Heart, label: 'Family Member', path: '/dashboard/family/member' },
          { icon: Calendar, label: 'Schedule', path: '/dashboard/family/schedule' },
          { icon: Radio, label: 'Care Tracking', path: '/dashboard/family' },
          { icon: BookOpen, label: 'Care Journals', path: '/care-journals' },
          { icon: Pill, label: 'Medications', path: '/medications' },
          { icon: FileText, label: 'Care Reports', path: '/dashboard/family/reports' },
        ]
      },
      {
        label: 'Communication',
        icon: MessageSquare,
        items: [
          { icon: MessageSquare, label: 'Messages', path: '/dashboard/family' },
          { icon: Camera, label: 'Photos', path: '/dashboard/family' },
          { icon: Megaphone, label: 'Broadcasts', path: '/broadcasts' },
        ]
      },
      {
        label: 'Requests',
        icon: ClipboardList,
        items: [
          { icon: ClipboardList, label: 'Shift Changes', path: '/dashboard/family' },
        ]
      },
    ]
  },
};

// Flat menu for simpler roles
const flatMenuItems = {
  developer: [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard/developer' },
    { icon: Building2, label: 'Companies', path: '/dashboard/developer?tab=companies' },
    { icon: Users, label: 'Users & Usage', path: '/dashboard/developer?tab=users' },
    { icon: BarChart3, label: 'Audit Log', path: '/dashboard/developer?tab=audit' },
  ]
};

// Collapsible nav group component
function NavGroup({ group, location, onNavigate }) {
  const hasActiveChild = group.items.some(item => location.pathname === item.path);
  const [open, setOpen] = useState(hasActiveChild);
  const GroupIcon = group.icon;

  useEffect(() => {
    if (hasActiveChild && !open) setOpen(true);
  }, [location.pathname]);

  return (
    <li>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200
          ${hasActiveChild
            ? 'text-[#00A6A0] bg-[#00A6A0]/5'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <GroupIcon className="w-4.5 h-4.5" strokeWidth={1.5} />
        <span className="flex-1 text-left text-sm font-medium">{group.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`} />
      </button>
      <div
        className="overflow-hidden transition-all duration-200"
        style={{
          maxHeight: open ? `${group.items.length * 44}px` : '0px',
          opacity: open ? 1 : 0,
        }}
      >
        <ul className="ml-3 pl-4 border-l border-border/50 mt-1 space-y-0.5">
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={onNavigate}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 text-sm
                    ${isActive
                      ? 'bg-[#00A6A0]/10 text-[#00A6A0] font-medium'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
}

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgRegion, setOrgRegion] = useState('UK');
  const spotlightCtx = useSpotlight();

  const role = user?.role;
  const isAdmin = ['agency_admin', 'ceo'].includes(role);

  // Fetch org region for sidebar filtering
  useEffect(() => {
    if (isAdmin && user?.organization_id) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/org/me`, { withCredentials: true })
        .then(r => setOrgRegion(r.data?.region || 'UK'))
        .catch(() => {});
    }
  }, [isAdmin, user?.organization_id]);

  // UK-specific paths to hide for non-UK regions
  const ukOnlyPaths = ['/dspt-dashboard', '/cqc-readiness', '/dspt-assessment', '/nhs-connect'];

  // Filter menu items based on region
  const filterByRegion = (items) => {
    if (orgRegion === 'UK' || !isAdmin) return items;
    return items.filter(item => !ukOnlyPaths.includes(item.path));
  };

  const rawConfig = roleMenuConfig[role];
  const config = rawConfig && isAdmin ? {
    ...rawConfig,
    groups: rawConfig.groups?.map(g => ({ ...g, items: filterByRegion(g.items) })).filter(g => g.items.length > 0),
  } : rawConfig;
  const flat = flatMenuItems[role];
  const useGrouped = !!config;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (role) => {
    const labels = {
      agency_admin: 'Agency Admin',
      ceo: 'Principal Admin',
      caregiver: 'Caregiver',
      client: 'Client',
      family: 'Family Member',
      developer: 'Platform Developer'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-background relative protected-content no-select" onContextMenu={e => e.preventDefault()}>
      {/* Glassmorphic background blobs */}
      <div className="glass-blobs"><div className="blob-3" /></div>
      <div className="noise-overlay" />

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 glass-nav flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-muted/50"
          data-testid="mobile-menu-toggle"
          aria-label="Open navigation menu"
        >
          <Menu className="w-6 h-6 text-foreground" />
        </button>
        
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="FendanaCura" className="h-8 w-auto" />
          <span className="text-lg font-heading font-bold text-[#00A6A0]">FendanaCura</span>
        </Link>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button className="relative p-2 rounded-lg hover:bg-muted/50" data-testid="notifications-btn">
            <Bell className="w-5 h-5 text-muted-foreground" />
            <span className="notification-dot" />
          </button>
        </div>
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-[100dvh] w-[280px] glass-sidebar z-50 transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="dashboard-sidebar"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo & Close (Mobile) */}
          <div className="p-6 flex items-center justify-between border-b border-border flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FendanaCura" className="h-10 w-auto" />
              <span className="text-xl font-heading font-bold text-foreground">FendanaCura</span>
            </Link>
            <button
              onClick={closeSidebar}
              className="lg:hidden p-2 rounded-lg hover:bg-muted"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-border flex-shrink-0">
            <Link to="/settings" onClick={closeSidebar}>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted hover:bg-muted/80 transition-colors">
                <div className="relative flex-shrink-0">
                  <Avatar className="h-10 w-10">
                    {user?.profile_photo_url ? (
                      <AvatarImage src={user.profile_photo_url} alt={user?.name} />
                    ) : (
                      <AvatarFallback className="bg-[#00A6A0]/20 text-[#00A6A0] font-medium">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  {user?.guide_badge && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-amber-500 border-2 border-background flex items-center justify-center" title="Guide Completed">
                      <Star className="w-2.5 h-2.5 text-white fill-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{getRoleLabel(user?.role)}</p>
                  {user?.staff_number && (
                    <p className="text-[10px] text-[#FF6F61] font-mono truncate">{user.staff_number}</p>
                  )}
                </div>
              </div>
            </Link>
          </div>

          {/* Navigation — scrollable */}
          <nav className="flex-1 p-3 overflow-y-auto sidebar-nav min-h-0" aria-label="Dashboard navigation">
            {useGrouped ? (
              <ul className="space-y-0.5">
                {/* Standalone items (Dashboard) */}
                {config.standalone.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={closeSidebar}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200
                          ${isActive
                            ? 'bg-[#00A6A0]/10 text-[#00A6A0]'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                        <span className="font-medium text-sm">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}

                {/* Divider */}
                <li className="py-1"><div className="h-px bg-border/40 mx-2" /></li>

                {/* Grouped items */}
                {config.groups.map((group) => (
                  <NavGroup
                    key={group.label}
                    group={group}
                    location={location}
                    onNavigate={closeSidebar}
                  />
                ))}
              </ul>
            ) : (
              <ul className="space-y-1">
                {(flat || []).map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={closeSidebar}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                          ${isActive
                            ? 'bg-[#00A6A0]/10 text-[#00A6A0]'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </nav>

          {/* Sidebar Footer — always visible, never scrolls */}
          <div className="flex-shrink-0 p-3 pb-20 lg:pb-3 border-t border-border/40 space-y-1">
            <Link to="/guide" onClick={closeSidebar}>
              <Button
                variant="ghost"
                className={`w-full justify-start gap-3 ${location.pathname === '/guide' ? 'text-[#00A6A0] bg-[#00A6A0]/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                data-testid="guide-btn"
              >
                <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                <span>Guide</span>
              </Button>
            </Link>
            <div className="flex gap-1">
              <Link to="/settings" onClick={closeSidebar} className="flex-1">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground hover:bg-muted"
                  data-testid="settings-btn"
                >
                  <Settings className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">Settings</span>
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="flex-1 justify-start gap-3 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[280px] pt-16 lg:pt-0 min-h-screen h-screen supports-[height:100dvh]:min-h-[100dvh] supports-[height:100dvh]:h-[100dvh] overflow-y-auto" role="main" aria-label="Main content">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 items-center justify-between px-8 glass-header border-b border-border/50">
          <div className="flex items-center gap-4">
            {user?.cisc && (
              <span className="px-3 py-1 rounded-full bg-muted border border-border text-xs text-muted-foreground">
                {user.cisc}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <button className="relative p-2 rounded-lg hover:bg-muted" data-testid="notifications-btn-desktop">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <span className="notification-dot" />
            </button>
            <Link to="/settings" className="p-2 rounded-lg hover:bg-muted" data-testid="settings-btn-desktop">
              <Settings className="w-5 h-5 text-muted-foreground" />
            </Link>
            <div className="w-px h-8 bg-border" />
            <Link to="/settings" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="relative flex-shrink-0">
                <Avatar className="h-9 w-9">
                  {user?.profile_photo_url ? (
                    <AvatarImage src={user.profile_photo_url} alt={user?.name} />
                  ) : (
                    <AvatarFallback className="bg-[#00A6A0]/20 text-[#00A6A0] text-sm font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  )}
                </Avatar>
                {user?.guide_badge && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 border-2 border-background flex items-center justify-center" title="Guide Completed">
                    <Star className="w-2 h-2 text-white fill-white" />
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.staff_number || user?.usic}</p>
              </div>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-8 pb-28 lg:pb-8 mobile-content-safe">
          {children}
        </div>
      </main>

      {/* SOS Button - show for caregivers, clients, and family */}
      {['caregiver', 'client', 'family'].includes(user?.role) && <SOSButton />}

      {/* Mobile Bottom Tab Navigation */}
      <MobileBottomNav />

      {/* Offline Indicator */}
      <OfflineBadge />

      {/* Floating Install/Share Button */}
      <FloatingInstallShare />

      {/* Global Spotlight Overlay */}
      {spotlightCtx?.spotlight && (
        <SpotlightOverlay
          target={spotlightCtx.spotlight.target}
          title={spotlightCtx.spotlight.title}
          text={spotlightCtx.spotlight.text}
          onClose={spotlightCtx.hideSpotlight}
          onNext={spotlightCtx.spotlight.nextStep ? () => spotlightCtx.advanceToNext(spotlightCtx.spotlight.nextStep) : undefined}
          hasNext={spotlightCtx.spotlight.hasNext}
        />
      )}
    </div>
  );
}

export default DashboardLayout;
